import {
  EuiBadge,
  EuiCommentList,
  EuiCommentProps,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiLink,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui';
import type { FunctionComponent } from 'react';
import { formatPhone, formatDateTime } from '@lib/utils';
import type { Person } from '@domain/person';

interface Props {
  person: Person;
}

const AuthnSection: FunctionComponent<Props> = ({ person }) => {
  const { idnumber, authn } = person;

  if (authn == null) {
    return (
      <>
        <EuiText>No authentication system account.</EuiText>
      </>
    );
  }

  const { cellnumber, email, emails, lastLogin, createdAt, adminChanges } =
    authn;

  const phoneDescrip = (
    <span>
      {formatPhone(cellnumber)}{' '}
      <EuiLink
        target="_blank"
        href={`https://login.voteda.org/view-as-admin/${idnumber}`}>
        Change
      </EuiLink>
    </span>
  );

  const emailDescrip = (
    <EuiFlexGroup direction="column" gutterSize="xs" wrap responsive={false}>
      {(emails ?? []).map((e, index) => (
        <div key={index}>
          <a href={`mailto:${e.email}`}>{e.email}</a>{' '}
          {e.email === email && <EuiBadge color="primary">Default</EuiBadge>}
          {e.isVerified ? (
            <EuiBadge color="success">Verified</EuiBadge>
          ) : (
            <EuiBadge color="danger">Not Verified</EuiBadge>
          )}
        </div>
      ))}
    </EuiFlexGroup>
  );

  const descriptionItems = [
    { title: 'Cell number', description: phoneDescrip },
    { title: 'Emails', description: emailDescrip },
    {
      title: 'Last login',
      description:
        lastLogin == null
          ? '(never)'
          : `${formatDateTime(new Date(lastLogin))} (SAST)`,
    },
  ];

  const firstChange: EuiCommentProps = {
    username: `${person.firstname} ${person.surname}`,
    event: `created account`,
    timestamp: `at ${formatDateTime(new Date(createdAt))}`,
  };

  const changes: EuiCommentProps[] = [
    firstChange,
    ...(adminChanges ?? []).map(change => ({
      username: `${change.adminUser.firstname} ${change.adminUser.surname}`,
      event: `changed OTP number`,
      timestamp: `at ${formatDateTime(new Date(change.timestamp))}`,
      children: (
        <div>
          From <EuiBadge>{formatPhone(change.fromNumber)}</EuiBadge> to{' '}
          <EuiBadge>{formatPhone(change.toNumber)}</EuiBadge> Reason:{' '}
          {change.reason}
        </div>
      ),
    })),
  ];

  if (authn.suspended && authn.suspensionDetails != null) {
    const suspension = authn.suspensionDetails;
    changes.push({
      username: `${suspension.adminUser.firstname} ${suspension.adminUser.surname}`,
      event: `suspended account`,
      timestamp: `at ${formatDateTime(new Date(suspension.timestamp))}`,
      children: <div>Reason: {suspension.reason}</div>,
      timelineAvatar: 'error',
      eventColor: 'danger',
    });
  }

  return (
    <>
      <EuiDescriptionList
        type="column"
        columnGutterSize="m"
        listItems={descriptionItems}
      />
      <EuiSpacer size="s" />
      <EuiTitle size="xs">
        <h4>History</h4>
      </EuiTitle>
      <EuiSpacer size="s" />
      <EuiCommentList comments={changes} />
    </>
  );
};

export default AuthnSection;
